import {
    Flex,
    Grid,
    useStyles,
    Button,
    pxToRem,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { Link, NavLink } from '@remix-run/react';

import * as S from './FeaturedSection.style';

import SectionHeader from '../section-header/SectionHeader';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { getResultTileLayoutMobile } from '@modules/articles/util/get-archizeen-tiles-layout';

import type { FeaturedSectionProps } from './FeaturedSection';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { useLocalisation } from '@archipro-website/localisation';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';

const FeaturedSectionMobile: React.FC<FeaturedSectionProps> = ({
    featuredSection,
    showFeaturedSlides,
}) => {
    const { css } = useStyles();

    const {
        featureMain,
        topArticles,
        title,
        link,
        description,
        featuredSlides,
    } = featuredSection;

    const {
        ID,
        title: featureMainTitle,
        label: featureMainLabel,
        date: featureMainDate,
        readTime: featureMainReadTime,
        image,
        professionalID: _professionalID,
        canEdit: _canEdit,
        bmEditorLink: _bmEditorLink,
        ...featureMainRest
    } = featureMain;

    const { localiseDate } = useLocalisation();
    const appDisplayMode = useAppDisplayModeContext();
    return (
        <div>
            <Flex className={css(S.Header)}>
                <SectionHeader title={title} content={description} />
            </Flex>
            <Flex column className={css(S.FeaturedArticleMobile)}>
                {featureMain && (
                    <ArchizeenTile
                        {...featureMainRest}
                        as={Link}
                        to={featureMain.link}
                        prefetch="intent"
                        variant="01"
                        size="xsmall"
                        article={featureMain}
                        title={{
                            children: featureMainTitle,
                            maximumLines: 3,
                        }}
                        label={{
                            children: featureMainLabel,
                            variant: '06',
                        }}
                        date={{
                            children: getFormattedLocalisedDate(
                                localiseDate,
                                featureMainDate
                            ),
                            variant: '06',
                        }}
                        readTime={{
                            children: featureMainReadTime,
                            variant: '06',
                        }}
                        image={{
                            src: image,
                            alt: featureMainTitle,
                            width: 414,
                            height: 414,
                            sizes: '100vw',
                        }}
                        impression={{
                            professionalID: featureMain.professionalID,
                            itemID: ID,
                            type: 'LibraryItem_Directory',
                        }}
                        variables={{
                            tilePadding: 0,
                        }}
                        articleId={ID}
                        appDisplayMode={appDisplayMode}
                    />
                )}
                <Grid columns={'100%'} className={css(S.ArticleGridMobile)}>
                    {topArticles.map((article, index) => {
                        const {
                            link,
                            ID,
                            professionalID,
                            title,
                            canEdit: _canEdit,
                            bmEditorLink: _bmEditorLink,
                            date,
                            ...rest
                        } = article;
                        const orientation = getResultTileLayoutMobile(index);
                        return (
                            <ArchizeenTile
                                key={ID}
                                as={Link}
                                to={link}
                                prefetch="intent"
                                {...rest}
                                variant="04"
                                articleId={ID}
                                date={getFormattedLocalisedDate(
                                    localiseDate,
                                    date
                                )}
                                size={
                                    orientation === 'portrait'
                                        ? 'medium'
                                        : 'small'
                                }
                                favouritePosition="card"
                                title={{
                                    children: title,
                                    maximumLines: 3,
                                }}
                                impression={{
                                    professionalID: professionalID,
                                    itemID: ID,
                                    type: 'LibraryItem_Directory',
                                }}
                                image={{
                                    width: 118,
                                    height: 131,
                                    src: article.image,
                                    alt: title,
                                }}
                            />
                        );
                    })}
                    <Button
                        className={css(S.ViewMore)}
                        size={16}
                        as={NavLink}
                        to={link}
                        prefetch={'intent'}
                        variables={{
                            buttonPadding: pxToRem(15),
                        }}
                        outlined
                        transparent
                    >
                        VIEW MORE {title.toUpperCase()}
                    </Button>
                </Grid>
            </Flex>
            {showFeaturedSlides && featuredSlides.length > 0 ? (
                <Flex className={css(S.CarouselContainer)}>
                    <FeaturedCarousel featuredSlides={featuredSlides} />
                </Flex>
            ) : null}
        </div>
    );
};

export default FeaturedSectionMobile;
